import { motion } from 'framer-motion';
import { Award, Users, Car, MapPin, Shield, Clock, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import PageHero from '../components/PageHero';

const EXTERIOR_IMG = 'https://customer-assets.emergentagent.com/job_spray-wash-preview/artifacts/eif4mi42_474949816_1065542595376121_1473048260461701712_n.jpg';
const HAND_WASH_IMG = 'https://customer-assets.emergentagent.com/job_spray-wash-preview/artifacts/swslw90w_480790144_1087613703169010_4496990203856420121_n.jpg';
const TEAM_IMG = 'https://customer-assets.emergentagent.com/job_spray-wash-preview/artifacts/m5g831cy_847e99a5-cf46-4a04-ad90-1f557f3e898a.jpeg';
const EVERWASH_URL = 'https://app.everwash.com/#membership-plans/143';

const fadeInUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] }
};

export const AboutPage = () => {
  return (
    <div className="overflow-hidden">
      {/* Hero */}
      <PageHero 
        badge="Our Story"
        title="About Super Spray"
        titleAccent="Car Wash II"
        subtitle="Tahlequah's original tunnel car wash. Full-service experience: hand wash first, then through the tunnel."
      />

      {/* Story Section */}
      <section className="bg-white py-20 md:py-32" data-testid="about-story">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div {...fadeInUp}>
              <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-[#0F172A] mb-6">
                The Original Tunnel Wash in Tahlequah
              </h2>
              <div className="space-y-6 text-lg text-slate-600 leading-relaxed">
                <p>
                  When Super Spray Car Wash II opened its doors, we became Tahlequah's first tunnel car wash. That pioneering spirit has driven us ever since - we're always looking for ways to serve our customers better.
                </p>
                <p>
                  Today, we're proud to be the only full-service car wash in Tahlequah that combines hand washing with our automated tunnel. Our staff gives your vehicle personal attention first, then we send you through the tunnel for a perfect finish - the best of both worlds.
                </p>
                <p>
                  Our commitment to quality means we only use the best equipment. Our Sonny's vehicle washing systems with Ryko SoftGloss and FoamBrite material deliver a thorough, safe clean every single time.
                </p>
              </div>
            </motion.div>

            <motion.div {...fadeInUp} className="relative">
              <div className="absolute -top-6 -right-6 w-full h-full bg-red-500 rounded-2xl" />
              <img 
                src={EXTERIOR_IMG} 
                alt="Super Spray Car Wash exterior"
                className="relative z-10 w-full h-[400px] object-cover rounded-2xl shadow-xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* What Makes Us Different */}
      <section className="bg-slate-50 py-20 md:py-32" data-testid="about-difference">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <span className="text-red-600 font-bold tracking-[0.2em] uppercase text-sm mb-4 block">
              Our Difference
            </span>
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-[#0F172A] mb-6">
              What Makes Us Special
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: Award,
                title: 'First in Tahlequah',
                desc: 'We were the original tunnel car wash in town. That experience shows in everything we do.'
              },
              {
                icon: Car,
                title: 'Hand + Tunnel Combo',
                desc: 'Hand wash attention first, then through our automated tunnel for the perfect finish.'
              },
              {
                icon: Shield,
                title: 'Premium Equipment',
                desc: 'Sonny\'s systems with Ryko SoftGloss - the industry standard for safe, effective cleaning.'
              },
              {
                icon: Users,
                title: 'Local Team',
                desc: 'We\'re your neighbors, not a faceless corporation. We take pride in serving our community.'
              },
              {
                icon: Clock,
                title: 'Convenient Hours',
                desc: 'Open 9 AM to 7 PM, seven days a week. We\'re here when you need us.'
              },
              {
                icon: MapPin,
                title: 'Easy Access',
                desc: 'Conveniently located on S Muskogee Ave with plenty of room for all vehicle sizes.'
              }
            ].map((item, idx) => (
              <motion.div
                key={idx}
                {...fadeInUp}
                transition={{ ...fadeInUp.transition, delay: idx * 0.1 }}
                className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-lg transition-shadow"
              >
                <div className="w-14 h-14 bg-[#1E3A8A] rounded-xl flex items-center justify-center mb-6">
                  <item.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-[#0F172A] mb-3">{item.title}</h3>
                <p className="text-slate-600">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="bg-[#0F172A] py-20 md:py-32 relative" data-testid="about-team">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div {...fadeInUp} className="order-2 lg:order-1">
              <img 
                src={TEAM_IMG} 
                alt="Super Spray team"
                className="w-full h-[450px] object-cover rounded-2xl" style={{ objectPosition: '50% 15%' }}
              />
            </motion.div>

            <motion.div {...fadeInUp} className="order-1 lg:order-2">
              <span className="text-red-500 font-bold tracking-[0.2em] uppercase text-sm mb-4 block">
                Our Team
              </span>
              <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-white mb-6">
                Friendly Faces, Quality Service
              </h2>
              <div className="space-y-6 text-lg text-slate-300 leading-relaxed">
                <p>
                  At Super Spray Car Wash II, we believe a great car wash starts with great people. Our team is trained to provide exceptional service with a smile.
                </p>
                <p>
                  We're not just washing cars - we're serving our neighbors. Many of our team members have been with us for years, and they take pride in their work.
                </p>
              </div>
              
              <div className="mt-8 grid grid-cols-2 gap-4">
                {[
                  'Trained professionals',
                  'Friendly service',
                  'Attention to detail',
                  'Community focused'
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-red-500" />
                    <span className="text-white">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Community Section */}
      <section className="bg-white py-20 md:py-32" data-testid="about-community">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div {...fadeInUp} className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-red-600 font-bold tracking-[0.2em] uppercase text-sm mb-4 block">
              Serving Our Community
            </span>
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-[#0F172A] mb-6">
              Proud to Serve Tahlequah
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed">
              We're more than a car wash - we're part of the Tahlequah community. From local events to everyday interactions, we're proud to serve our neighbors.
            </p>
          </motion.div>

          <motion.div {...fadeInUp} className="grid md:grid-cols-2 gap-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_spray-wash-preview/artifacts/l8vkxykw_Screenshot%202026-04-24%20at%202.34.18%E2%80%AFPM.png" 
              alt="Super Spray Car Wash serving Tahlequah"
              className="w-full aspect-square object-cover object-center rounded-2xl"
            />
            <div className="flex flex-col justify-center">
              <h3 className="text-2xl font-bold text-[#0F172A] mb-4">Serving the Area</h3>
              <p className="text-slate-600 mb-6">
                While we're based in Tahlequah, we serve customers from all over the region - Fort Gibson, Muskogee, Wagoner, Park Hill, Keys, Hubert, and beyond.
              </p>
              <p className="text-slate-600">
                Whether you're a local or just passing through, you'll get the same great service and sparkling clean results.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#DC2626] py-20 md:py-24 relative overflow-hidden" data-testid="about-cta">
        <motion.div 
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }}
          animate={{ x: [0, 60], y: [0, 60] }}
          transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
        />
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center relative z-10">
          <motion.div {...fadeInUp}>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white mb-6">
              Experience the Difference
            </h2>
            <p className="text-xl text-red-100 mb-8 max-w-2xl mx-auto">
              Come see why Tahlequah has trusted us for years. Visit today or join EverWash for unlimited washes.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href={EVERWASH_URL} target="_blank" rel="noopener noreferrer" data-testid="about-everwash-cta">
                <Button className="w-full sm:w-auto bg-white text-[#DC2626] hover:bg-slate-100 font-bold text-lg px-10 py-6 rounded-full">
                  Join EverWash
                </Button>
              </a>
              <Link to="/contact" data-testid="about-contact-btn">
                <Button variant="outline" className="w-full sm:w-auto border-2 border-white text-white hover:bg-white/10 font-bold text-lg px-10 py-6 rounded-full">
                  Visit Us Today
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
